import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { colors } from '@/design-system/colors';
import { navigationItems, filterNavigation } from './navigation-items';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useNavigationState } from './use-navigation-state';
import { CompanyLogoBox } from './CompanyLogoBox';
import { CompanySwitcher } from './CompanySwitcher';

interface SidebarNavigationProps {
  onNavigate: (path: string) => void;
  onAIChatOpen?: () => void;
}

export function SidebarNavigation({ onNavigate, onAIChatOpen }: SidebarNavigationProps) {
  const { openMenus, handleItemClick, handleSubmenuClick } = useNavigationState({ onNavigate, onAIChatOpen });
  const { can } = usePermissions();
  // only what the role may VIEW (the server refuses the rest anyway)
  // and only what the navigation matrix of the role (in this company) leaves ON
  const { isVisibleKey } = useNavigation();
  const visibleItems = useMemo(() => filterNavigation(navigationItems, can, isVisibleKey), [can, isVisibleKey]);

  return (
    <div
      // BUG-16 fix (2026-09-19): `w-sidebar` (see tailwind.config.js) is a
      // real fixed width + `flex-shrink-0`, replacing the old `min-w-64`
      // (min-width only, no shrink guard -- worked in practice because
      // nothing here ever pushed against it, but wasn't an explicit
      // guarantee). `hidden md:flex` -- below md, this component renders
      // nothing at all; MobileNavigation (mobile-navigation.tsx) is the
      // <md replacement, mounted alongside this one in App.tsx.
      className="hidden md:flex w-sidebar flex-shrink-0 flex-col sidebar-navigation"
      style={{ backgroundColor: colors.sidebar }}
    >
      <div className="flex h-full flex-col gap-2 p-4 overflow-y-auto">
        <CompanyLogoBox />
        <CompanySwitcher />
        {visibleItems.map((item) => (
          <div key={item.title} className="relative">
            <button
              id={item.title === 'Project Management' ? 'nav-project-management' : undefined}
              onClick={() => handleItemClick(item)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 nav-header transition-colors"
              style={{
                backgroundColor: openMenus.includes(item.title) && item.submenu
                  ? colors.sidebarActive
                  : 'transparent',
                color: colors.sidebarText
              }}
              onMouseEnter={(e) => {
                if (!(openMenus.includes(item.title) && item.submenu)) {
                  e.currentTarget.style.backgroundColor = colors.sidebarHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!(openMenus.includes(item.title) && item.submenu)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex items-center min-w-0">
                {React.cloneElement(item.icon as React.ReactElement, { className: "mr-2 h-4 w-4 flex-shrink-0", style: { color: colors.sidebarText } })}
                <span className="nav-header truncate" style={{ color: colors.sidebarText }}>{item.title}</span>
              </div>
              {item.submenu && (
                openMenus.includes(item.title) ?
                  <ChevronRight className="h-4 w-4 flex-shrink-0 rotate-90" style={{ color: colors.sidebarText }} /> :
                  <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: colors.sidebarText }} />
              )}
            </button>

            {item.submenu && openMenus.includes(item.title) && (
              <div className="pl-6 pt-1 pb-1">
                <div className="flex flex-col gap-1 rounded-md p-2 mt-1" style={{ backgroundColor: colors.sidebar }}>
                  {item.submenu.map((subItem) => (
                    <button
                      id={subItem.title === "To-do's" ? 'nav-todos' : undefined}
                      key={subItem.title}
                      onClick={(e) => handleSubmenuClick(e, subItem, item.title)}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 nav-subheader justify-start transition-colors min-w-0"
                      style={{ color: colors.sidebarText }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.sidebarHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {React.cloneElement(subItem.icon as React.ReactElement, { className: "h-4 w-4 flex-shrink-0", style: { color: colors.sidebarText } })}
                      <span className="nav-subheader truncate" style={{ color: colors.sidebarText }}>{subItem.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
